import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPartyHorn } from '@fortawesome/pro-solid-svg-icons/faPartyHorn';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { RECOMMENDATION_YES } from '../../models/ReviewFlowStore';
import { BackgroundLayout } from './components/BackgroundLayout';

export const Success = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const flow = stores.reviewFlow;
  const parentId = pathId ? Number(pathId) : undefined;

  useEffect(() => {
    document.title = 'UrbanSitter - Thanks';
  }, []);

  useEffect(() => {
    if (!parentId) return;
    if (!flow.isValidForParent(parentId)) {
      navigate(`/parent/${parentId}/feedback`, { replace: true });
    }
  }, [parentId, flow, navigate]);

  const review = flow.review;
  const positive = review?.recommendation?.id === RECOMMENDATION_YES;

  const done = (): void => {
    navigate('/caregiver/dashboard');
  };

  return (
    <BackgroundLayout>
      <div className='us-card rounded-[20px] text-center'>
        {positive ? (
          <>
            <FontAwesomeIcon icon={faPartyHorn} className='text-5xl mb-4 text-emerald' />
            <h1 className='text-2xl font-semibold mb-4'>
              {t('pages.parentFeedback.success.positiveHeading')}
            </h1>
            <p>{t('pages.parentFeedback.success.positiveBody')}</p>
          </>
        ) : (
          <>
            <h1 className='text-2xl font-semibold mb-4'>
              {t('pages.parentFeedback.success.neutralHeading')}
            </h1>
            <p className='mb-2'>{t('pages.parentFeedback.success.neutralBody1')}</p>
            <p className='mb-2'>{t('pages.parentFeedback.success.neutralBody2')}</p>
            <p>
              {t('pages.parentFeedback.success.neutralBody3Prefix')}{' '}
              <a href='mailto:support@urbansitter.com' className='underline'>
                support@urbansitter.com
              </a>
            </p>
          </>
        )}

        <div className='mt-8'>
          <Button buttonType={ButtonType.primary} onClick={done}>
            {t('pages.parentFeedback.success.done')}
          </Button>
        </div>
      </div>
    </BackgroundLayout>
  );
});
